import { useState, useEffect } from 'react';
import { isValidFantasyPlayer } from '../utils/playerValue';

// Hook to fetch and cache player projections with localStorage fallback
export function useProjections() {
  const [projections, setProjections] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Clear the cache to force fresh calculations for debugging
    // TODO: Remove this once projections are working correctly
    localStorage.removeItem('projectionsCache');
    localStorage.removeItem('projectionsTimestamp');
    
    const cache = localStorage.getItem('projectionsCache');
    const ts = localStorage.getItem('projectionsTimestamp');
    const now = Date.now();
    if (cache && ts && now - parseInt(ts) < 24 * 60 * 60 * 1000) {
      try {
        const parsedCache = JSON.parse(cache);
        // Ensure keys are strings (JSON.parse might interpret numeric-like strings as numbers for keys if not careful)
        const stringKeyCache: Record<string, number> = {};
        for (const key in parsedCache) {
            stringKeyCache[String(key)] = parsedCache[key];
        }
        setProjections(stringKeyCache);
      } catch (e) {
        console.error("Failed to parse projections from cache", e);
        localStorage.removeItem('projectionsCache'); // Clear corrupted cache
        localStorage.removeItem('projectionsTimestamp');
      }
      setLoading(false);
      return;
    }

    async function fetchProjections() {
      try {
        console.log('Fetching player data from Sleeper...');
        
        // Use Sleeper's players endpoint instead of projections
        const res = await fetch('https://api.sleeper.app/v1/players/nfl');
        
        if (!res.ok) {
          throw new Error(`Failed to fetch players: ${res.status}`);
        }
        
        const playersData = await res.json();
        console.log(`Received player data for useProjections:`, Object.keys(playersData).length, 'players');
        
        // Get 2024 season stats for better projection basis (last completed season)
        let seasonStats: any = {};
        try {
          const statsRes = await fetch('https://api.sleeper.app/v1/stats/nfl/regular/2024');
          if (statsRes.ok) {
            seasonStats = await statsRes.json();
            console.log('Loaded 2024 season stats for projection enhancement');
          }
        } catch (e) {
          console.log('Could not load season stats, using basic projections');
        }

        // Create projections based on player data and position
        const map: Record<string, number> = {};
        
        // Define elite players and their 2024-2025 season projections
        const elitePlayerNames: Record<string, number> = {
          // Elite QBs (2024-2025 season)
          'Josh Allen': 380, 'Lamar Jackson': 350, 'Jalen Hurts': 340, 'Patrick Mahomes': 320,
          'Joe Burrow': 310, 'Justin Herbert': 300, 'Dak Prescott': 290, 'Tua Tagovailoa': 285,
          'Anthony Richardson': 280, 'Jayden Daniels': 275, 'C.J. Stroud': 270, 'Kyler Murray': 265,
          
          // Elite RBs (2024-2025 season) 
          'Christian McCaffrey': 320, 'Saquon Barkley': 280, 'Breece Hall': 270, 'Bijan Robinson': 265,
          'Jonathan Taylor': 260, 'Derrick Henry': 255, 'Josh Jacobs': 250, 'Kenneth Walker III': 245,
          'De\'Von Achane': 240, 'Alvin Kamara': 235, 'Aaron Jones': 230, 'Joe Mixon': 225,
          'James Cook': 220, 'Kyren Williams': 215, 'David Montgomery': 210, 'Rhamondre Stevenson': 205,
          
          // Elite WRs (2024-2025 season)
          'CeeDee Lamb': 290, 'Tyreek Hill': 285, 'Amon-Ra St. Brown': 280, 'A.J. Brown': 275,
          'Ja\'Marr Chase': 270, 'Justin Jefferson': 290, 'Stefon Diggs': 265, 'DK Metcalf': 260,
          'Puka Nacua': 255, 'Mike Evans': 250, 'Davante Adams': 245, 'Cooper Kupp': 240,
          'Keenan Allen': 235, 'DeVonta Smith': 230, 'Tee Higgins': 225, 'Chris Godwin': 220,
          'Calvin Ridley': 215, 'Terry McLaurin': 210, 'Amari Cooper': 205, 'Drake London': 200,
          'Garrett Wilson': 195, 'DJ Moore': 190, 'Jaylen Waddle': 185, 'DeAndre Hopkins': 170,
          
          // Elite TEs (2024-2025 season)
          'Travis Kelce': 200, 'Sam LaPorta': 185, 'Mark Andrews': 180, 'George Kittle': 175,
          'T.J. Hockenson': 170, 'Kyle Pitts': 165, 'Dallas Goedert': 160, 'Evan Engram': 155,
          'Jake Ferguson': 150, 'David Njoku': 145, 'Pat Freiermuth': 140, 'Tyler Higbee': 135,
          
          // Elite Defenses (2024-2025 season)
          'San Francisco 49ers': 160, 'Dallas Cowboys': 155, 'Buffalo Bills': 150, 'Pittsburgh Steelers': 145,
          'Baltimore Ravens': 140, 'Cleveland Browns': 135, 'Miami Dolphins': 130, 'Philadelphia Eagles': 125
        };

        // Team strength multipliers
        const teamStrengthMultipliers: Record<string, number> = {
          'KC': 1.15, 'BUF': 1.12, 'CIN': 1.10, 'LAC': 1.08, 'BAL': 1.08,
          'PHI': 1.10, 'SF': 1.12, 'DAL': 1.08, 'NYG': 1.05, 'MIN': 1.05,
          'TB': 1.06, 'MIA': 1.08, 'LAR': 1.05, 'SEA': 1.06, 'DEN': 1.03,
          'LV': 1.04, 'NE': 1.02, 'JAX': 1.04, 'TEN': 1.02, 'IND': 1.03,
          'CLE': 1.05, 'PIT': 1.04, 'NO': 1.03, 'ATL': 1.04, 'CAR': 1.01,
          'WAS': 1.02, 'DET': 1.06, 'GB': 1.07, 'CHI': 1.02, 'ARI': 1.02,
          'HOU': 1.03, 'NYJ': 1.03
        };
        
        Object.values(playersData).forEach((player: any) => {
          // Use centralized validation function
          if (player.player_id && isValidFantasyPlayer(player)) {
            const playerIdStr = player.player_id; // player_id is already a string
            const playerName = player.full_name || `${player.first_name || ''} ${player.last_name || ''}`.trim() || `Player ${playerIdStr}`;
            
            // Skip players with no meaningful name
            if (!playerName || playerName === 'Player undefined' || playerName.length < 2) {
              return;
            }
            
            // Check if this is an elite player
            let baseProjection = elitePlayerNames[playerName];
            
            // Debug logging for a few key players
            const debugPlayers = ['Josh Allen', 'Lamar Jackson', 'Justin Herbert', 'Tua Tagovailoa'];
            if (debugPlayers.includes(playerName)) {
              console.log(`DEBUG ${playerName}: Elite projection = ${baseProjection}, Position = ${player.position}, Team = ${player.team}`);
            }
            
            if (!baseProjection) {
              // Generate baseline projections based on position and player status
              const yearsFactor = Math.min(player.years_exp || 0, 8) / 8; // Cap experience benefit
              const ageFactor = player.age ? Math.max(0.7, Math.min(1.2, (32 - player.age) / 10)) : 1.0;
              
              // Get player's 2024 stats if available
              const playerStats = seasonStats[playerIdStr];
              const statsFactor = playerStats ? Math.min(2.0, 1 + (playerStats.fpts_ppr || 0) / 200) : 1.0;
              
              switch (player.position) {
                case 'QB':
                  baseProjection = (180 + yearsFactor * 60) * ageFactor * statsFactor;
                  break;
                case 'RB':
                  baseProjection = (120 + yearsFactor * 80) * ageFactor * statsFactor;
                  break;
                case 'WR':
                  baseProjection = (100 + yearsFactor * 70) * ageFactor * statsFactor;
                  break;       
                case 'TE':
                  baseProjection = (80 + yearsFactor * 60) * ageFactor * statsFactor;
                  break;
                case 'K':
                  baseProjection = 90 + (yearsFactor * 20);
                  break;
                case 'DEF':
                  baseProjection = 80 + (yearsFactor * 15);
                  break;
                default:
                  baseProjection = 0;
              }
            }
            
            // Apply team strength multiplier
            const teamMultiplier = teamStrengthMultipliers[player.team] || 1.0;
            baseProjection *= teamMultiplier;
            
            // Add player-specific variation based on name hash for consistency
            let consistentVariation = 1.0; // Default to no variation
            if (playerName && typeof playerName === 'string' && playerName.length > 0) {
              const nameHash = playerName.split('').reduce((a: number, b: string) => { 
                a = ((a << 5) - a) + b.charCodeAt(0); 
                return a & a; 
              }, 0);
              consistentVariation = 0.9 + ((Math.abs(nameHash) % 20) / 100); // 0.9 to 1.09 multiplier
            }
            baseProjection *= consistentVariation;
            
            // Ensure active team players get bonus
            if (player.team && player.team !== 'FA') {
              baseProjection *= 1.05;
            }
            
            // Additional age adjustment for non-elite players
            if (!elitePlayerNames[playerName] && player.age) {
              if (player.age < 25) baseProjection *= 1.03;
              else if (player.age > 30) baseProjection *= 0.97;
              else if (player.age > 32) baseProjection *= 0.92;
            }
            
            const finalProjection = Math.round(Math.max(0, baseProjection));
            map[playerIdStr] = finalProjection;
            
            // Debug logging for key players
            if (debugPlayers.includes(playerName)) {
              console.log(`DEBUG ${playerName}: Final projection = ${finalProjection}, Team multiplier = ${teamMultiplier}, Variation = ${consistentVariation.toFixed(3)}`);
            }
          }
        });
        
        console.log(`Generated projections for ${Object.keys(map).length} players in useProjections`);
        
        localStorage.setItem('projectionsCache', JSON.stringify(map));
        localStorage.setItem('projectionsTimestamp', now.toString());
        setProjections(map);
        
      } catch (e: any) {
        console.error('Error in useProjections fetchProjections:', e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    
    fetchProjections();
  }, []);

  return { projections, loading, error };
}
